import { useState, FormEvent } from "react";
import { motion } from "framer-motion";
import { Loader2, MessageCircleQuestion, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useReducedMotionSafe } from "@/components/system";
import { usePortalClient } from "@/components/portal/use-portal-client";
import { useCreateRequest } from "@/hooks/use-requests";

/**
 * "Domanda veloce": il filo diretto col team, sempre a portata di mano.
 * Non è un assistente automatico — dall'altra parte c'è una persona.
 */
export function QuickQuestion() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const create = useCreateRequest();
  const { client, accent } = usePortalClient();
  const reduced = useReducedMotionSafe();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const message = text.trim();
    if (!message || !client) return;
    try {
      await create.mutateAsync({
        title: message.slice(0, 80),
        description: message.length > 80 ? message : null,
        priority: "medium",
        client_id: client.id,
      });
      toast.success("Messaggio inviato: il team è già stato avvisato");
      setText("");
      setOpen(false);
    } catch {
      toast.error("Non siamo riusciti a inviare il messaggio. Riprova tra un istante.");
    }
  }

  return (
    <>
      <motion.button
        type="button"
        initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen(true)}
        aria-label="Scrivi una domanda veloce al team"
        title="Domanda veloce"
        className="fixed bottom-6 left-4 z-30 hidden h-11 items-center gap-2 rounded-full border border-primary/20 bg-card-2/90 px-4 backdrop-blur-xl transition-[border-color,transform] duration-fast ease-palazzo hover:-translate-y-0.5 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex"
      >
        <span
          aria-hidden
          className="flex h-6 w-6 items-center justify-center rounded-full"
          style={{
            background: `color-mix(in srgb, ${accent} 14%, transparent)`,
            color: accent,
          }}
        >
          <MessageCircleQuestion className="h-3.5 w-3.5" />
        </span>
        <span className="text-xs font-medium">Domanda veloce</span>
      </motion.button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5">
              <span
                aria-hidden
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-primary"
              >
                <MessageCircleQuestion className="h-4 w-4" />
              </span>
              Domanda veloce
            </DialogTitle>
            <DialogDescription className="font-serif text-[15px] italic leading-relaxed">
              Due righe bastano. Il team riceve una notifica e ti risponde qui nel portale.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Es. Riuscite a darmi un aggiornamento sulla campagna?"
              rows={4}
              aria-label="Il tuo messaggio"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Per una richiesta più strutturata usa la sezione <strong>Richieste</strong>.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" variant="gold" disabled={create.isPending || !text.trim()}>
                {create.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-4 w-4" aria-hidden />
                )}
                Invia
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
